import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../../store/store";
import { customListWorkspaces } from "../../g-process/component/helper/Helper";
import { getJobTitlesFromWorkspaceId } from "../../../components/GroupOrganizationTreeSelect/helper";
import { AccountApi } from "../../../api/AccountApi";

export type DepartmentType = {
  id: number;
  name: string;
  status?: number;
};

export type GroupUsersType = {
  id: number;
  name: string;
};

export type JobTitleType = {
  id: number;
  name: string;
};

export type JobTitleShortType = { id: number; name: string };

export type RoleType = {
  code: string;
  created_at: string;
  creator: string | null;
  id: number;
  level: number;
  modifier: string | number | null;
  name: string;
  status: number;
  updated_at: string | number;
};

export type User = {
  id: number;
  user_name?: string;
  full_name: string;
  email: string;
  name?: string;
  status?: number;
  code?: string;
  employee?: {
    id?: number;
    code?: string;
  };
  workspace?: {
    id?: number;
  };
  roles?: {
    id?: number;
  };
  job_title?: {
    id?: number;
  };
};

type Role = {
  code: string;
  created_at: string;
  creator: string | null;
  id: number;
  level: number;
  modifier: string | number | null;
  name: string;
  status: number;
  updated_at: string | number;
};

// Member of group
export type Member = {
  id: number;
  user_name: string;
  name: string | null;
  full_name: string;
};

export type Group = {
  id: number;
  name: string;
  email: string | null;
  status: number;
  member: Member[];
  leader: Member[];
};

type Owner = {
  id: number;
  user_name: string;
  full_name: string;
};

export type Workspace = {
  id: number;
  level: number;
  name: string;
  title?: string;
  value?: string;
  key?: number;
  code?: string;
  children: Workspace[];
  status?: number;
  owner: Owner;
  type?: {
    code: string;
  };
};
export type StepTransferConditionType = {
  created_at: string;
  updated_at: string;
  group_condition_id: null | number;
  id: number;
  next_step_type: number;
  step_condition: number;
  step_id: number;
  step_order: number;
};
export type StepTransferConditionsType = StepTransferConditionType[];

type metaInfoState = {
  departments: DepartmentType[];
  users: User[];
  fetchUsersAndGroupsStatus: "idle" | "loading" | "succeeded" | "failed";
  okrDensityAllocate: any[];
  roles: Role[];
  titles: JobTitleType[];
  groups: Group[];
  status: "idle" | "loading" | "succeeded" | "failed";
  workspaces: Workspace[];
};

const initialState: metaInfoState = {
  departments: [],
  users: [],
  fetchUsersAndGroupsStatus: "idle",
  okrDensityAllocate: [],
  roles: [],
  titles: [],
  groups: [],
  status: "idle",
  workspaces: [],
};

const mapUserFields = (user: any): User => {
  const mappedUser: User = {
    id: Number(user?.id ?? 0),
    user_name: user?.user_name,
    full_name: user?.full_name,
    name: user?.name,
    email: user?.email,
    status: user?.status,
    code: user?.code,
    employee: user?.employee
      ? { id: user?.employee?.id, code: user?.employee?.code }
      : undefined,
    workspace: user?.workspace ? { id: user?.workspace?.id } : undefined,
    roles: user?.roles ? { id: user?.roles?.id } : undefined,
    job_title: user?.job_title ? { id: user?.job_title?.id } : undefined,
  };
  return mappedUser;
};

export const fetchAllUsersAndGroups = createAsyncThunk(
  "meta-info/fetchAllUsersAndGroups",
  async (): Promise<[User[], Group[]]> => {
    try {
      const [usersRes, groupsRes] = await Promise.all([
        AccountApi.getUsersModule(),
        AccountApi.getListGroups(),
      ]);
      const users = Array.isArray(usersRes?.data)
        ? usersRes.data.map(mapUserFields)
        : [];
      return [users, groupsRes.data];
    } catch (error) {
      // Handle error if needed
      throw error;
    }
  }
);

export const fetchMetaInfoCore = createAsyncThunk(
  "meta-info/fetchMetaInfoCore",
  async (): Promise<{ roles: Role[]; workspaces: Workspace[] }> => {
    try {
      const [rolesRes, workspacesRes] = await Promise.all([
        AccountApi.getListRoles(),
        AccountApi.getListWorkspaces(),
      ]);
      const roles = Array.isArray(rolesRes?.data) ? rolesRes.data : [];
      const workspaces = Array.isArray(workspacesRes?.data)
        ? workspacesRes.data
        : [];
      return { roles, workspaces };
    } catch (error) {
      throw error;
    }
  }
);

const metaInfoSlice = createSlice({
  name: "metaInfo",
  initialState,
  reducers: {
    setOkrDensityAllocate: (state, action) => {
      state.okrDensityAllocate = action.payload;
    },
    setMetaInfoAfterLogin: (state, action) => {
      const { roles, workspaces } = action.payload;
      state.roles = roles;
      state.workspaces = workspaces;
      state.departments = customListWorkspaces(workspaces);
      state.titles = getJobTitlesFromWorkspaceId(workspaces, 1);
      state.status = "succeeded";
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchMetaInfoCore.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchMetaInfoCore.fulfilled, (state, action) => {
        const { roles, workspaces } = action.payload;
        state.roles = roles;
        state.workspaces = workspaces;
        state.departments = customListWorkspaces(workspaces);
        state.titles = getJobTitlesFromWorkspaceId(workspaces, 1);
        state.status = "succeeded";
      })
      .addCase(fetchMetaInfoCore.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(fetchAllUsersAndGroups.fulfilled, (state, action) => {
        const [users, groups] = action.payload;
        state.users = users;
        state.groups = groups;
        state.fetchUsersAndGroupsStatus = "succeeded";
      })
      .addCase(fetchAllUsersAndGroups.pending, (state) => {
        state.fetchUsersAndGroupsStatus = "loading";
      })
      .addCase(fetchAllUsersAndGroups.rejected, (state) => {
        state.fetchUsersAndGroupsStatus = "failed";
      });
  },
});

export default metaInfoSlice.reducer;

export const { setOkrDensityAllocate, setMetaInfoAfterLogin } =
  metaInfoSlice.actions;

export const selectDepartments = (state: RootState) =>
  state.metaInfo.status === "succeeded" ? state.metaInfo.departments : [];
export const selectGroups = (state: RootState) =>
  state.metaInfo.fetchUsersAndGroupsStatus === "succeeded"
    ? state.metaInfo.groups
    : [];
export const selectRoles = (state: RootState) =>
  state.metaInfo.status === "succeeded" ? state.metaInfo.roles : [];
export const selectUsers = (state: RootState) =>
  state.metaInfo.fetchUsersAndGroupsStatus === "succeeded"
    ? state.metaInfo.users
    : [];
export const selectFetchUsersAndGroupsStatus = (state: RootState) =>
  state.metaInfo.fetchUsersAndGroupsStatus;
export const selectTitles = (state: RootState) =>
  state.metaInfo.status === "succeeded" ? state.metaInfo.titles : [];
export const selectWorkspaces = (state: RootState) =>
  state.metaInfo.status === "succeeded" ? state.metaInfo.workspaces : [];

export const selectOkrDensityAllocate = (state: RootState) =>
  state.metaInfo.status === "succeeded"
    ? state.metaInfo.okrDensityAllocate
    : [];
export const selectMetaInfoStatus = (state: RootState) => {
  const { status, fetchUsersAndGroupsStatus } = state.metaInfo;
  if (status === "failed" || fetchUsersAndGroupsStatus === "failed") {
    return "failed";
  }
  if (status === "loading" || fetchUsersAndGroupsStatus === "loading") {
    return "loading";
  }
  if (status === "succeeded" && fetchUsersAndGroupsStatus === "succeeded") {
    return "succeeded";
  }
  return "idle";
};
